import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { create } from "zustand";

import type { WhatsAppConversation } from "./api";
import { useConversations } from "./hooks";

const PREFS_KEY = "wa-alert-prefs";

type AlertPrefs = {
  soundEnabled: boolean;
  onlyWhenAiOff: boolean;
  setSoundEnabled: (v: boolean) => void;
  setOnlyWhenAiOff: (v: boolean) => void;
};

function loadPrefs(): { soundEnabled: boolean; onlyWhenAiOff: boolean } {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { soundEnabled: true, onlyWhenAiOff: false, ...JSON.parse(raw) };
  } catch {
    // fall through to defaults
  }
  return { soundEnabled: true, onlyWhenAiOff: false };
}

function savePrefs(p: { soundEnabled: boolean; onlyWhenAiOff: boolean }) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // storage unavailable — prefs just won't persist
  }
}

export const useAlertPrefs = create<AlertPrefs>((set, get) => ({
  ...loadPrefs(),
  setSoundEnabled: (v) => {
    set({ soundEnabled: v });
    savePrefs({ soundEnabled: v, onlyWhenAiOff: get().onlyWhenAiOff });
  },
  setOnlyWhenAiOff: (v) => {
    set({ onlyWhenAiOff: v });
    savePrefs({ soundEnabled: get().soundEnabled, onlyWhenAiOff: v });
  },
}));

/** Total unread across all WhatsApp conversations (for the sidebar badge). */
export function useWhatsAppUnread(): number {
  const { data } = useConversations();
  return (data ?? []).reduce((sum, c) => sum + (c.unread_count || 0), 0);
}

function playBeep() {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.15);
    osc.connect(gain);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
    osc.onended = () => void ctx.close();
  } catch {
    // Audio blocked (no user gesture yet) — the toast still shows.
  }
}

/**
 * Global listener mounted in AppLayout: watches the shared conversations query
 * and fires a beep + toast whenever a conversation's unread count goes up
 * (i.e. a new customer message arrived), on any page of the app.
 */
export default function WhatsAppAlerts() {
  const { data } = useConversations();
  const { soundEnabled, onlyWhenAiOff } = useAlertPrefs();
  const location = useLocation();
  const navigate = useNavigate();
  // null until the first snapshot — never alert on initial load.
  const prevUnread = useRef<Map<string, number> | null>(null);

  useEffect(() => {
    if (!data) return;
    const snapshot = new Map(data.map((c) => [c.id, c.unread_count || 0]));
    const prev = prevUnread.current;
    prevUnread.current = snapshot;
    if (!prev) return;

    const fresh = data.filter(
      (c) => (c.unread_count || 0) > (prev.get(c.id) ?? 0),
    );
    const alertable = onlyWhenAiOff ? fresh.filter((c) => !c.ai_enabled) : fresh;
    if (alertable.length === 0) return;

    if (soundEnabled) playBeep();
    // On the Conversations page the new message is already visible.
    if (location.pathname !== "/conversations") {
      const c: WhatsAppConversation = alertable[0];
      toast(
        alertable.length > 1
          ? `${alertable.length} new WhatsApp messages`
          : `New WhatsApp message — ${c.customer_name || c.customer_number}`,
        {
          description: alertable.length > 1 ? undefined : c.last_message_preview,
          action: { label: "Open", onClick: () => navigate("/conversations") },
        },
      );
    }
  }, [data, soundEnabled, onlyWhenAiOff, location.pathname, navigate]);

  return null;
}
